/**
 * Phippa Squad — Variant Gallery Filter
 * Filters the product media gallery to show only the selected color's images.
 * Works on all products. Add this to your Balance theme via:
 *   Online Store → Themes → Edit Code → Assets → variant-gallery-filter.js
 *
 * Then add this line before </body> in layout/theme.liquid:
 *   <script src="{{ 'variant-gallery-filter.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  // Only run on product pages
  if (!document.querySelector('media-gallery')) return;

  // ─── Get the current product's handle from the URL ───────────────────────
  var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
  if (!match) return;
  var handle = match[1];

  // ─── Fetch product JSON ───────────────────────────────────────────────────
  fetch('/products/' + handle + '.js')
    .then(function (r) { return r.json(); })
    .then(function (product) {
      var maps = buildMaps(product);

      // Nothing to filter if no color data found
      if (!Object.keys(maps.mediaColorMap).length) return;

      // Filter on initial load
      filterGallery(getVariantId(), maps);

      // Watch for Balance replacing <media-gallery> on variant switch
      observeGalleryReplacement(maps);

      // Listen for variant selector changes
      listenForVariantChanges(maps);
    })
    .catch(function () {
      // Silently fail — don't break the page if fetch fails
    });

  // ─── Build all maps needed for color-based filtering ─────────────────────
  function buildMaps(product) {
    var variants = product.variants || [];
    var media    = product.media    || [];
    var options  = product.options  || [];

    // Find which option position holds "Color" / "Colour"
    var colorIdx = -1;
   for (var o = 0; o < options.length; o++) {
     if (/colou?r/i.test(options[o].name)) { colorIdx = o; break; }
   }
   // No Color option on this product — skip filtering entirely
   if (colorIdx === -1) return { variantColorMap: {}, mediaColorMap: {} };

    // variantColorMap: variantId (string) → color string (lower-cased)
    var variantColorMap = {};
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var color = (colorIdx === 0 ? v.option1 : colorIdx === 1 ? v.option2 : v.option3) || '';
      variantColorMap[String(v.id)] = color.toLowerCase();
    }

    // mediaColorMap: mediaId (string) → color string (lower-cased)
    // Step 1 — seed from variant featured_media assignments (most reliable)
    var mediaColorMap = {};
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (v.featured_media && v.featured_media.id) {
        var mid   = String(v.featured_media.id);
        var color = variantColorMap[String(v.id)];
        if (color && !mediaColorMap[mid]) {
          mediaColorMap[mid] = color;
        }
      }
    }

    // Step 2 — fill in unassigned media (back images) using their alt text.
    // In Shopify the alt on each product image is typically set to the color name.
    for (var j = 0; j < media.length; j++) {
      var m   = media[j];
      var mid = String(m.id);
      if (mediaColorMap[mid]) continue; // already assigned above

      if (m.alt) {
        // Match alt text against a known color: look for the longest color name
        // that appears (as a whole word) in the alt string.
        var altLower  = m.alt.toLowerCase();
        var bestColor = null;
        var bestLen   = 0;
        var colorSet  = Object.values ? Object.values(variantColorMap) : objectValues(variantColorMap);
        for (var k = 0; k < colorSet.length; k++) {
          var c = colorSet[k];
          if (!c || c.length <= bestLen) continue;
          // Match whole-word so "red" doesn't match "dark red" wrong
          var re = new RegExp('(^|\\W)' + escapeRe(c) + '(\\W|$)');
          if (re.test(altLower)) { bestColor = c; bestLen = c.length; }
        }
        if (bestColor) mediaColorMap[mid] = bestColor;
      }

      // Step 3 — final fallback: check the image filename/URL for color names
      if (!mediaColorMap[mid]) {
        var src      = (m.src || '').toLowerCase().split('?')[0]; // strip query params
        var colorSet2 = Object.values ? Object.values(variantColorMap) : objectValues(variantColorMap);
        var bestColor2 = null;
        var bestLen2   = 0;
        for (var k = 0; k < colorSet2.length; k++) {
          var c = colorSet2[k];
          if (!c || c.length <= bestLen2) continue;
          // Color in URL typically separated by hyphens: "hoodie-royal-back"
          var slugColor = c.replace(/\s+/g, '-');
          if (src.indexOf(slugColor) !== -1 || src.indexOf(c.replace(/\s+/g, '_')) !== -1) {
            bestColor2 = c; bestLen2 = c.length;
          }
        }
        if (bestColor2) mediaColorMap[mid] = bestColor2;
      }
    }

    return { variantColorMap: variantColorMap, mediaColorMap: mediaColorMap };
  }

  // ─── Get the currently selected variant ID ────────────────────────────────
  function getVariantId() {
    var input = document.querySelector('input[name="id"]');
    return input ? input.value : null;
  }

  // ─── Show/hide gallery items based on selected variant's color ────────────
  function filterGallery(variantId, maps) {
    if (!variantId) return;

    var selectedColor = maps.variantColorMap[String(variantId)];
    if (!selectedColor) return;

    // Balance puts data-media-id on an inner div inside <li class="product-media-container">
    var items = document.querySelectorAll('media-gallery [data-media-id]');
    if (!items.length) return;

    items.forEach(function (item) {
      var mediaId    = String(item.dataset.mediaId);
      var mediaColor = maps.mediaColorMap[mediaId];

      // Show if: image has no color association (truly generic product shot)
      //       OR image's color matches the selected variant's color
      var show = !mediaColor || mediaColor === selectedColor;

      // Hide the parent <li>, not the inner div — so the empty slot disappears
      var listItem = item.closest('li') || item.parentElement;
      listItem.style.display = show ? '' : 'none';
    });
  }

  // ─── Watch for Balance replacing the <media-gallery> element ─────────────
  // Balance fetches new variant page HTML and swaps <media-gallery> in full.
  // Re-apply filtering after each replacement.
  function observeGalleryReplacement(maps) {
    var gallery = document.querySelector('media-gallery');
    if (!gallery) return;
    var parent = gallery.parentElement;
    if (!parent) return;

    var observer = new MutationObserver(function () {
      setTimeout(function () {
        filterGallery(getVariantId(), maps);
      }, 50);
    });
    observer.observe(parent, { childList: true });
  }

  // ─── Listen for variant selection changes ─────────────────────────────────
  function listenForVariantChanges(maps) {
    // Standard Shopify hidden input change
    document.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'id') {
        filterGallery(e.target.value, maps);
      }
    });

    // Balance custom events (names vary by theme version)
    var events = ['theme:variant:update', 'variant:changed', 'variantChange'];
    events.forEach(function (eventName) {
      document.addEventListener(eventName, function (e) {
        var id = (e.detail && (
          (e.detail.resource && e.detail.resource.id) ||
          (e.detail.variant  && e.detail.variant.id)
        )) || getVariantId();
        if (id) filterGallery(String(id), maps);
      });
    });
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────
  function escapeRe(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  function objectValues(obj) {
    return Object.keys(obj).map(function (k) { return obj[k]; });
  }
})();